import React from "react";
import { KDialog, KIcon } from "@kyburz/kdesign-system";
import useSafeSetState from "../../hooks/useSafeSetState";
import { DAMAGE_PREVIEW_SIZE, useStyles } from "./DamagesViewStyles";
import { Incident } from "../../interfaces/IncidentInterface";
import { DAMAGE_COLORS } from "../constants/incidents";

import DamagePopup from "./DamagePopup";

interface Props {
  incident: Incident;
  setDamages: (value) => void;
}

const DamagesView = ({ incident, setDamages }: Props) => {
  const classes = useStyles();
  const initState = {
    selectedDamageIndex: -1,
    selectedDamage: null,
    selectedPhoto: null,
    selectedCoordinates: null,
  };
  const [
    { selectedDamageIndex, selectedPhoto, selectedCoordinates, selectedDamage },
    setState,
  ] = useSafeSetState({ ...initState });

  const selectDamage = (e, index) => {
    e.stopPropagation();
    if (index !== selectedDamageIndex) setState({ selectedDamageIndex: index });
    else setState({ selectedDamageIndex: -1 });
  };

  const deleteDamage = (index) => {
    const damages = [...incident.damages];
    damages.splice(index, 1);
    setDamages(damages);
  };

  const editDamage = (index) =>
    setState({
      selectedDamage: {
        ...incident.damages[index],
      },
      selectedDamageIndex: index,
    });

  const selectPhoto = (src?: string) => setState({ selectedPhoto: src });

  const getDamageMarks = () => {
    const damages = incident.damages || [];
    return damages.map((d, index) => {
      const markStyle = {
        backgroundColor: DAMAGE_COLORS[d.damage_type],
      };
      const activeMarkClass = `${classes.mark}--${
        index === selectedDamageIndex && "active"
      }`;
      return (
        <button
          className={classes.markContainer}
          style={{
            left: `${d.rel_x * 100}%`,
            top: `${d.rel_y * 100}%`,
          }}
          onClick={(e) => selectDamage(e, index)}
          key={`damage-mark${d.damage_type}${index}`}
          type="button"
        >
          <div
            className={`${classes.mark} ${activeMarkClass}`}
            style={markStyle}
          />
        </button>
      );
    });
  };

  const getDamagesList = () => {
    const damages = incident.damages || [];
    if (damages.length === 0) {
      return (
        <span className={classes.damageListHint}>
          There are no damages. Click on the image to add a new one.
        </span>
      );
    }
    return damages.map((d, index) => {
      const photos = d.photos.map((p, i) => (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <img
          key={p + i}
          src={p.image || p}
          alt={d.description}
          className={classes.damagePhoto}
          onClick={() => selectPhoto(p.image || p)}
        />
      ));
      const inactive =
        selectedDamageIndex > -1 && index !== selectedDamageIndex
          ? "inactive"
          : "";
      const damageStyle = {
        borderColor: DAMAGE_COLORS[d.damage_type],
      };
      const damageLabelStyle = {
        color: DAMAGE_COLORS[d.damage_type],
      };
      return (
        <div
          className={`${classes.damage} ${classes.damage}--${inactive}`}
          style={damageStyle}
          key={`damage-list${d.damage_type}${index}`}
        >
          <div>
            <div className={classes.damageType} style={damageLabelStyle}>
              {d.damage_type}
            </div>
            <div>{d.description}</div>
            {photos}
          </div>
          <div className={classes.damageActions}>
            <span className={classes.damageAction}>
              <KIcon icon="edit" onClick={() => editDamage(index)} />
            </span>
            <span className={classes.damageAction}>
              <KIcon icon="trash-2" onClick={() => deleteDamage(index)} />
            </span>
          </div>
        </div>
      );
    });
  };

  const putDamage = (e) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const rel_x = (e.clientX - bounds.left) / DAMAGE_PREVIEW_SIZE;
    const rel_y = (e.clientY - bounds.top) / DAMAGE_PREVIEW_SIZE;
    setState({ selectedCoordinates: { rel_x, rel_y } });
  };

  const cancelDamageAdding = () => setState({ ...initState });

  const saveDamage = (data) => {
    if (selectedDamage) {
      const newDamages = [...incident.damages];
      newDamages[selectedDamageIndex] = { ...data };
      setDamages(newDamages);
    } else {
      const damage = {
        ...data,
        ...selectedCoordinates,
      };
      setDamages([damage, ...incident.damages]);
    }
    setState({ ...initState });
  };

  return (
    <div className={classes.container}>
      <div className={classes.damagesContainer}>
        <div className={classes.damagesPreview} onClick={putDamage}>
          <img
            src={incident?.vehicle_type?.skeleton}
            alt={incident?.vehicle_type?.type}
            className={classes.skeleton}
          />
          {getDamageMarks()}
        </div>
      </div>
      <div className={classes.damageList}>{getDamagesList()}</div>
      <KDialog
        title="Damage photo"
        open={!!selectedPhoto}
        size="md"
        secondaryCTAonClick={() => selectPhoto()}
        secondaryCTAText="Close"
      >
        <img
          src={selectedPhoto}
          alt="Damage"
          className={classes.viewDamagePhoto}
        />
      </KDialog>
      <DamagePopup
        open={!!selectedCoordinates || !!selectedDamage}
        cancel={cancelDamageAdding}
        save={saveDamage}
        initDamage={selectedDamage}
      />
    </div>
  );
};

export default DamagesView;
